import axios from "@/plugins/axios";
import qs from "qs";
import utils from "@/stores/utils";

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20 };

const createState = (options = {}) => {
    return {
        invitations: [],
        invitations_filters: DEFAULT_FILTERS,
        invitations_count: 0,
        invitations_loaded: false,
        invitations_loading: false,
        invitations_loading_errors: null,

        invitation_deleting: false,
        invitation_deleting_errors: null,

        invitation_saving: false,
        invitation_saving_errors: null,
        cancelTokenSource: null,
        
        _storageOptions: options || {},
    };
};

const mutations = {
    updateInvitations(state, invitations) {
        state.invitations = invitations;
        state.invitations_loaded = true;
    },
    updateInvitationsCount(state, count) {
        state.invitations_count = count;
    },
    updateInvitationsFilters(state, filters) {
        state.invitations_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.invitations_filters, fieldsToSave);
        }
    },
    updateInvitationsLoading(state, loading) {
        state.invitations_loading = loading;
    },
    updateInvitationsLoadingErrors(state, errors) {
        state.invitations_loading_errors = errors;
    },

    updateInvitationDeleting(state, deleting) {
        state.invitation_deleting = deleting;
    },
    updateInvitationDeletingErrors(state, errors) {
        state.invitation_deleting_errors = errors;
    },
    updateInvitationsLoaded(state, loaded) {
        state.invitations_loaded = loaded;
    },

    updateInvitationSaving(state, saving) {
        state.invitation_saving = saving;
    },
    updateInvitationSavingErrors(state, errors) {
        state.invitation_saving_errors = errors;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};

const actions = {
    async deleteInvitation({ commit, dispatch, state }, params) {

        const url = `/api/invitations/${params.instance.uuid}/`;

        commit("updateInvitationDeleting", true);
        commit("updateInvitationDeletingErrors", null);

        try {
            const response = await axios.delete(url);
            commit("updateInvitationDeleting", false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateInvitationDeleting", false);
            commit("updateInvitationDeletingErrors", error.details);
            throw error;
        }
    },

    async fetchInvitations({ commit, dispatch, state, rootGetters }, params) {
        if (rootGetters['session/current_user_permissions'].indexOf("core.view_invitation") === -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit("updateInvitationsLoading", true);
        commit("updateInvitationsLoadingErrors", null);

        try {
            const response = await axios.get("/api/invitations/", {
                params: state.invitations_filters,
                cancelToken: source.token,
            });
            commit("updateInvitationsLoading", false);
            commit("updateInvitations", response.data.results);
            commit("updateInvitationsCount", response.data.count);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit("updateInvitationsLoadingErrors", error.details);
            commit("updateInvitationsLoading", false);
            throw error;
        }
    },

    async saveInvitation({ commit, dispatch, state, rootGetters }, params) {
        if (rootGetters['session/current_user_permissions'].indexOf("core.add_invitation") === -1) {
            return;
        }

        let url = "/api/invitations/";
        let method = axios.post;

        commit("updateInvitationSaving", true);
        commit("updateInvitationSavingErrors", null);

        try {
            const response = await method(url, params.data);
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit("updateInvitationSavingErrors", error.details);
            throw error;
        } finally {
            commit("updateInvitationSaving", false);
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit("updateInvitationsLoaded", false);
        commit("updateInvitations", []);
        commit("updateInvitationsCount", 0);
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit("updateStorageOptions", params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit("updateInvitationsFilters", {
            ...{ offset: 0, limit: 20 },
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params?.filters || {}),
        });
    },
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions,
});
